import _, { findIndex, findLastIndex, get, last } from "lodash";
import { DateTime, Interval } from "luxon";
import { toLuxon } from "shared/dateTimeUtils";
import { action, computed, makeObservable, observable } from "mobx";
import tinycolor from "tinycolor2";
import {
  getAlarms,
  getChartHeight,
  getUniqueOrderedSegments,
} from "./MonitorUtils";
import config from "../../config";
import {
  fetchJpgSnippetPreview,
  getAcwaVersion,
  getMonitorConfig,
  getMonitorEvents,
  getMonitorMeasurements,
  getMonitorTrends,
  getSnippetEventDescriptionLibrary,
  getSnippetPreview,
  postOpsLog,
  postQrsProcessor,
  savePMSnippet,
  validateSnippetParameters,
  getAhiDescriptions,
  getPatientAhiData,
} from "../../serviceCalls";

import documentLabels from "./documentLabels";
import { limitGamut } from "../Surveil/colorMap";

export default class MonitorStore {
  constructor({ asmpid, suppressDiscreteRow, isEmbedded }) {
    makeObservable(this, {
      loading: observable,
      loadingMeasurements: observable,
      loadingSnippet: observable,
      snippetLoaded: observable,
      config: observable,
      events: observable,
      loadingEvents: observable,
      hasNoTele: observable,
      _measurements: observable,
      isEmbedded: observable,
      timeOffset: observable,
      enableSelectTime: observable,
      mouseDownX: observable,
      pixelsPerSecond: observable,
      offsetY: observable,
      offsetX: observable,
      _liveMode: observable,
      millisecondsPassed: observable,
      chartLag: observable,
      timeAgoDuration: observable,
      selectedWaveform1key: observable,
      selectedWaveform2key: observable,
      seconds: observable,
      markers: observable,
      startPosition: observable,
      endPosition: observable,
      boundaries: observable,
      diagnoses: observable,
      marchout: observable,
      selectedDiagnoses: observable,
      controlsExpanded: observable,
      isAllLeads: observable,
      isAllLeadsRender: observable,
      posting: observable,
      customDescription: observable,
      pdfCanvasZoomWidth: observable,
      showStatementCodes: observable,
      enableFreeText: observable,
      snippetDocumentTypes: observable,
      selectedDocTypeOption: observable,
      renderingCharts: observable,
      documentLabels: observable,
      pdf: observable,
      discretesOnSnippet: observable,
      isPQRSTBoundaryBreach: observable,
      snippetsWaveforms: computed,
      scrubberWidth: computed,
      measurementMode: computed,
      caliperMode: computed,
      liveMode: computed,
      startLiveMode: action,
      updateMeasurementBuffers: action,
      fetchMonitorMeasurements: action,
      zoom: computed,
      PR: observable,
      QRS: observable,
      QT: observable,
      caliperDistance: observable,
      ahiDescriptions: observable,
      ahiData: observable,
      hrKey: computed,
      centerDateTime: computed,
    });

    this.asmpid = asmpid;
    this.isEmbedded = isEmbedded;
    this.suppressDiscreteRow = suppressDiscreteRow;
    this.augmentDiscrete = this.augmentDiscrete.bind(this);
    this.chartAspect = this.pixelsPerSecond / this.chartHeight;
    this.saveSnippet = this.saveSnippet.bind(this);
    this.getSnippetPreview = this.getSnippetPreview.bind(this);
    this.getMonitorConfig();
    // TODO: see about removing this.
    this.updateMeasurementBuffers = _.debounce(
      this.updateMeasurementBuffers,
      500,
      {
        maxWait: 1000,
        leading: true,
        trailing: true,
      },
    );

    documentLabels.promise
      .then(() => {
        this.snippetDocumentTypes = Object.keys(
          documentLabels.snippetDocumentTypes,
        )
          .filter((key) => config.pm.showShiftAssessment || key !== "2") // Remove "Shift Assessment" type.
          .map((key) => ({
            label: documentLabels.snippetDocumentTypes[key],
            value: key,
          }));
        this.selectedDocTypeOption = this.snippetDocumentTypes.find(
          (t) => t.value === "3",
        ); // Preselect Event type.
      })
      .catch(_.noop);
  }

  liveModeDelaySeconds = config.pm.liveModeDelaySeconds;
  panningBufferSeconds = 10;
  loading;
  loadingMeasurements;
  config;
  events;
  loadingEvents;
  hasNoTele;
  /*
          TODO: Making _measurements observable due to Tools not rendering correctly. AS1-4243

          Previously we used the below to avoid deep comparisons on measurements to avoid jank in
          the way the measurements are rendered in the UI.
          ```
            _measurements = observable({}, null, { deep: false });
          ```
        */
  _measurements;
  isEmbedded = false;
  timeOffset;

  enableSelectTime;

  mouseDownX;

  get initialPixelsPerSecond() {
    return config.defaultMonitorZoom
      ? (125 * config.defaultMonitorZoom) / 0.25
      : 125;
  }

  pixelsPerSecond = this.initialPixelsPerSecond;

  get chartHeight() {
    return getChartHeight({ pixelsPerSecond: this.pixelsPerSecond });
  }

  get defaultOffsetY() {
    return -65;
  }
  offsetY = this.defaultOffsetY;
  offsetX = 0;
  _liveMode = true;
  millisecondsPassed;
  chartLag = { time: "", value: 0 };
  serverLatency;
  timeAgoDuration;

  // Tools
  selectedWaveform1key;
  selectedWaveform2key;
  seconds;
  markers = [];
  startPosition = { x: 0, y: 0 };
  endPosition = { x: 0, y: 0 };
  boundaries;
  diagnoses = [];
  marchout = false;
  selectedDiagnoses = [];
  controlsExpanded;

  // CreateStatement
  isAllLeads = config.pm.allLeadsDefault;
  isAllLeadsRender = config.pm.allLeadsDefault;
  posting = false;
  customDescription = "";
  pdfCanvasZoomWidth = 1190;
  showStatementCodes = true;
  enableFreeText = true;
  snippetDocumentTypes = [];
  selectedDocTypeOption = { value: 3 };
  renderingCharts;
  documentLabels = documentLabels;
  pdf;
  discretesOnSnippet = [];
  isPQRSTBoundaryBreach = false;
  loadingSnippet = false;
  snippetLoaded = false;

  ahiDescriptions = [];
  ahiData;

  get minPixelsPerSecond() {
    return 50;
  }

  get maxPixelsPerSecond() {
    return 500;
  }

  get snippetsWaveforms() {
    if (this.isAllLeads) {
      return this.config.waveGroups;
    }
    return this.toolsWaveforms.filter(Boolean);
  }

  get toolsWaveforms() {
    return [
      this.config?.waveGroups.find((g) => g.key === this.selectedWaveform1key),
      this.config?.waveGroups.find((g) => g.key === this.selectedWaveform2key),
    ].filter(Boolean);
  }

  get scrubberWidth() {
    return this.endPosition.x - this.startPosition.x;
  }

  get measurementMode() {
    return this.markers.length === 4;
  }

  get caliperMode() {
    return this.markers.length === 2;
  }

  clearMarkers() {
    this.markers = [];
    this.savedMarkers = undefined;
  }

  /**
   *
   * @param {DateTime} chartStartTime
   * @param {DateTime} chartEndTime
   * @param chartStartTimeSeconds
   * @returns
   */
  sliceWaveforms({
    chartStartTime,
    chartEndTime = this.chartEndTime,
    offset = 1,
  }) {
    if (!this.measurements) {
      return [];
    }
    let firstIndex = -1,
      lastIndex = -1;
    if (chartStartTime) {
      const startTime = chartStartTime.minus({
        seconds: this.liveMode ? this.liveModeDelaySeconds : offset,
      });
      firstIndex = findIndex(
        this.measurements?.data,
        (segment) => DateTime.fromISO(segment.timestamp) >= startTime,
      );
    }
    // If we're in live-mode, we want all the rest of the data. We don't need to find a last-index.
    if (this.liveMode) return this.measurements?.data?.slice(firstIndex);
    if (chartEndTime) {
      const endTime = chartEndTime.plus({ seconds: 2 });
      lastIndex = findLastIndex(
        this.measurements?.data,
        (segment) => DateTime.fromISO(segment.timestamp) <= endTime,
      );
    }
    if (firstIndex === -1 || lastIndex === -1) {
      return [];
    }
    return this.measurements.data.slice(firstIndex, lastIndex + 1);
  }

  validateSnippetParameters(patient) {
    if (!patient) return;
    return validateSnippetParameters(patient);
  }

  hasData() {
    return (
      get(this.measurements, "data.length") &&
      get(this.measurements, "data[0].timestamp")
    );
  }

  get liveMode() {
    return this._liveMode;
  }

  set liveMode(isLive) {
    if (this._liveMode !== isLive) {
      this._measurements = this.dropEmptyFutureData(this);
    }
    this._liveMode = !!isLive;
  }

  get isSnippetWorklist() {
    return Boolean(sessionStorage.snippetWorklistPatient);
  }

  get isBaselineWorklist() {
    return Boolean(sessionStorage.baselineWorklist);
  }

  get discretes() {
    return [...this.discretesRow, ...this.discreteMeasurements];
  }

  get discretesRow() {
    return this.suppressDiscreteRow
      ? []
      : _.chain(get(this.config, "discreteGroups", []))
          .filter(
            (discrete) =>
              discrete.layoutDiscreteDirection === "layoutDiscretesBottom",
          )
          .map(this.augmentDiscrete())
          .orderBy((d) => !d.values.filter(_.identity).length)
          .orderBy(["isLocked", "locked"], ["desc", "desc"])
          .value();
  }

  augmentDiscrete() {
    let data = this.measurements?.data ?? [];
    const lastIndex = findLastIndex(
      data,
      (seg) => seg.dateTime <= this.chartEndTime,
    );
    return (discrete) => {
      let time;

      const slicedData = data
        .slice(0, lastIndex)
        .slice(discrete.isEpisodic ? undefined : -3);

      const values = discrete.keys.map((key) => {
        let value;
        slicedData.findLast((measurement) =>
          measurement?.discreteData.find((discreteData) => {
            const found = discreteData.key === key;
            if (found) {
              value = discreteData.data;
              time = discreteData.time;
            }
            return found;
          }),
        );
        return value;
      });

      return {
        ...discrete,
        values,
        time,
      };
    };
  }

  getAlarms(chartStartTime) {
    return getAlarms(
      this.sliceWaveforms({
        chartStartTime,
        offset: this.chartWidth / this.pixelsPerSecond || 1,
      }),
    );
  }

  get discreteMeasurements() {
    return _.chain(get(this.config, "discreteGroups", []))
      .filter((discrete) => {
        if (this.suppressDiscreteRow) return true;
        return discrete.layoutDiscreteDirection === "layoutDiscretesRight";
      })
      .map(this.augmentDiscrete())
      .orderBy((d) => !d.values.filter(_.identity).length)
      .orderBy(["isLocked", "locked"], ["desc", "desc"])
      .value();
  }

  get snippetDiscretes() {
    return this.config?.discreteGroups
      ?.filter((d) => d.onSnippet)
      .map((d) => {
        const closests = d.keys.map((d) => this.getClosestDiscrete(d));
        const values = closests.map((d) => d?.value);
        const timestamp = closests.find((c) => c?.timestamp)?.timestamp;
        const time = closests.findLast((x) => x?.time)?.time;
        return {
          ...d,
          values,
          timestamp,
          time,
        };
      });
  }

  sliceMeasurements({ start, end }) {
    const startIndex = start
      ? findIndex(this.measurements.data, (seg) => seg.timestamp >= start)
      : 0;
    const lastIndex = end
      ? findLastIndex(this.measurements.data, (seg) => seg.timestamp <= end)
      : this.measurements.data.length - 1;
    return this.measurements.data.slice(startIndex, lastIndex);
  }

  get hrKey() {
    return (
      this.config?.discreteGroups?.find(
        (discrete) => discrete.vitalType === "HR",
      )?.keys?.[0] ||
      this.config?.discreteGroups?.find((discrete) =>
        discrete.title.includes("HR"),
      )?.keys?.[0]
    );
  }

  get centerDateTime() {
    let centerDateTime;
    if (this.measurementMode) {
      centerDateTime = DateTime.fromISO(
        this.markers?.[1]?.timestamp.toString(),
      );
    } else {
      let startTime, endTime;
      let lengthSeconds;

      if (this.boundaries?.length) {
        lengthSeconds = this.seconds.value;
        startTime = this.boundaries?.[0].timestamp.toString();
        endTime = this.boundaries?.[1].timestamp.toString();
        if (typeof startTime === "string")
          startTime = DateTime.fromISO(startTime);
        if (typeof endTime === "string") endTime = DateTime.fromISO(endTime);
      } else {
        lengthSeconds = this.chartWidth / this.pixelsPerSecond || 0;
        startTime = this.chartEndTime.minus({ seconds: lengthSeconds });
      }

      centerDateTime = startTime.plus({ seconds: lengthSeconds / 2 });
    }
    return centerDateTime;
  }

  getHeartRate() {
    if (!(this.hrKey && this.measurements)) return;
    return this.getClosestDiscrete(this.hrKey, this.asmpid);
  }

  getClosestDiscrete(key, asmpid, centerDateTime = this.centerDateTime) {
    const { cache } = this.getClosestDiscrete;
    // read from cache
    const centerSecond = Math.round(centerDateTime?.toSeconds());
    if (cache?.key !== centerSecond || cache.asmpid !== asmpid) {
      this.getClosestDiscrete.cache = { key: centerSecond, asmpid: asmpid };
    } else if (cache[key]) {
      return cache[key];
    }

    const dataLength = this.measurements?.data?.length;
    if (!dataLength) return;
    let centerIndex = findIndex(
      this.measurements?.data,
      (segment) => segment?.dateTime >= centerDateTime,
    );
    if (centerIndex === -1) centerIndex = this.measurements.data.length - 1;

    // indexes is an array of all indexes of measurements.data in order of distance from the centerIndex.
    const indexes = Object.keys(Array(dataLength).fill()).sort(
      (a, b) => Math.abs(a - centerIndex) - Math.abs(b - centerIndex),
    );

    let discreteRecord;

    for (let index of indexes) {
      const segment = this.measurements?.data?.[index];
      const discrete = segment?.discreteData?.find((d) => d.key === key);
      if (discrete) {
        discreteRecord = {
          value: discrete.data,
          timestamp: segment.timestamp,
          centerDateTime,
          time: discrete.time,
        };
        break;
      }
    }

    // write to cache
    this.getClosestDiscrete.cache[key] = discreteRecord;

    return discreteRecord;
  }

  get measurements() {
    return this._measurements;
  }

  set measurements(measurements) {
    this.setMeasurements(measurements);
  }

  transformMeasurements(measurements) {
    // add dateTime to each data
    measurements.data.forEach(addDateTimeToSegment);
    if (!measurements) return this.measurements;
    let oldData = [];
    if (measurements.reset) {
      measurements.reset = undefined;
    } else {
      oldData = this.measurements?.data || [];
    }
    // The server sometimes sends null or emptyString for bookmark, which may cause issues.
    // valid bookmarks are usually strings of numbers "0" to "99"
    measurements.bookmark = measurements.bookmark || undefined;
    // combine old data and new data
    measurements.data = getUniqueOrderedSegments([
      ...oldData,
      ...measurements.data,
    ]);

    return measurements;
  }

  resetMeasurements() {
    this._measurements = undefined;
    this.fetchedTimeRange = null;
    this.abortController?.abort();
  }
  setMeasurements = (measurements) => {
    if (!measurements) return;
    if (!measurements.data) return;

    const _measurements = this.transformMeasurements(measurements);

    if (!this._measurements?.data?.length || _measurements.reset) {
      this.consumeOffsetX();
    }
    this._measurements = measurements;
  };

  consumeOffsetX() {
    if (this.offsetX) {
      this.chartEndTime = this.originalChartEndTime.plus({
        seconds: this.offsetX / this.pixelsPerSecond,
      });
      this.originalChartEndTime = this.chartEndTime;
    }
    this.offsetX = 0;
  }

  snapToEnd({ liveMode = true } = {}) {
    let timestamp = last(this.measurements?.data)?.timestamp;

    if (timestamp) {
      const now = DateTime.fromISO(timestamp);
      this.chartEndTime = now.minus({
        seconds: this.liveModeDelaySeconds,
      });
      this.originalChartEndTime = this.chartEndTime;
      this.animationStartTime = performance.now();
    }
    this.liveMode = liveMode;
  }

  startLiveMode({ seconds = 20 } = {}) {
    // jump to end of data
    this._measurements = {};
    this.fetchedTimeRange = undefined;
    this.markers = [];
    this.boundaries = undefined;
    return this.fetchMonitorMeasurements(
      {
        seconds: _.clamp(
          Math.ceil(seconds + this.liveModeDelaySeconds),
          this.liveModeDelaySeconds,
          100,
        ),
      },
      true,
    ).then((res) => {
      this.snapToEnd();
      return res;
    });
  }

  trimHistory(chartLengthSeconds) {
    if (typeof chartLengthSeconds !== "number") return;
    this.chartLengthSeconds = chartLengthSeconds;
    // clear out old history
    if (!this.chartEndTime?.isValid) return;
    const dataStartTime =
      this.measurements?.data?.length && this.measurements?.data?.[0]?.dateTime;
    if (!dataStartTime?.isValid) return;
    const chartLengthPixels = this.pixelsPerSecond * chartLengthSeconds;
    const maxChartLengthSeconds = chartLengthPixels / this.minPixelsPerSecond;
    const chartStartTime = this.chartEndTime.minus({
      seconds: chartLengthSeconds || 0,
    });
    const minBufferBufferSeconds =
      maxChartLengthSeconds * this.chartToBufferScale + 1 || 0;
    const maxBufferSeconds = minBufferBufferSeconds * 1.2 + 1 || 0;
    // clear out data in the past (panning forwards or live mode)
    if (dataStartTime < chartStartTime.minus({ seconds: maxBufferSeconds })) {
      const index = this.measurements.data.findIndex(
        (segment) =>
          segment.dateTime.plus({ seconds: minBufferBufferSeconds }) >=
          chartStartTime,
      );
      if (index && index !== -1) {
        this.measurements.data = this.measurements.data.slice(index);
        this.fetchedTimeRange = this.fetchedTimeRange?.set({
          start: this.measurements.data[0].dateTime,
        });
      }
    }

    // clear data too far in the future, (when panning backwards).
    if (!this.liveMode) {
      if (
        _.last(this.measurements.data).dateTime >=
        this.chartEndTime.plus({ seconds: maxBufferSeconds })
      ) {
        this.measurements.data = this.measurements.data.filter(
          (segment) =>
            segment.dateTime <
            this.chartEndTime.plus({ seconds: minBufferBufferSeconds }),
        );
        if (this.measurements.data.length) {
          this.fetchedTimeRange = this.fetchedTimeRange?.set({
            end: _.last(this.measurements.data).dateTime,
          });
        } else {
          this.fetchedTimeRange = undefined;
        }
      }
    }
  }

  trimHistoryLive({ width }) {
    if (isNaN(width)) return;
    const maxBufferSeconds =
      width / this.minPixelsPerSecond + this.liveModeDelaySeconds * 3;
    if (this.fetchedTimeRange) this.fetchedTimeRange = undefined;
    if (this._measurements?.data?.length > maxBufferSeconds)
      this._measurements.data =
        this._measurements.data.slice(-maxBufferSeconds);
  }
  // TODO: `lastWaveSet` is not used, should delete
  get lastWaveSet() {
    let data = get(this.measurements, "data", []);
    return get(last(data), "waveData") || [];
  }

  /**
   * Constant value to determine the size of the buffer relative to the visible chart.
   * Used for cache control. */
  get chartToBufferScale() {
    return 2;
  }

  liveModeBufferSeconds(pixelWidth) {
    return Math.ceil(
      pixelWidth / this.minPixelsPerSecond + this.liveModeDelaySeconds,
    );
  }

  updateMeasurementBuffers({
    pixelWidth,
    pixelsPerSecond = this.pixelsPerSecond,
    chartEndTime = this.chartEndTime,
    bufferSeconds: bufferSecondsOverride,
  } = {}) {
    if (this.loadingMeasurements) {
      return;
    }
    const chartWidthSeconds = pixelWidth / pixelsPerSecond;
    const bufferSeconds =
      bufferSecondsOverride ?? chartWidthSeconds * this.chartToBufferScale;

    if (this.liveMode) {
      const bookmark = this.measurements?.bookmark;
      let query = {};
      const isBookmarked = !!bookmark;
      if (isBookmarked) query = { bookmark };
      else query = { seconds: this.liveModeBufferSeconds(pixelWidth) };

      return this.fetchMonitorMeasurements(query).then(() => {
        if (isBookmarked) {
          this.handleDrift();
        } else {
          this.snapToEnd();
        }
      });
    }

    // If chartEndTime is undefined, set it to the current time.
    const chartEndDateTime = chartEndTime?.isValid
      ? chartEndTime
      : DateTime.now().minus({ seconds: this.liveModeDelaySeconds });
    const chartStartTime = chartEndDateTime
      ?.minus({ seconds: pixelWidth / pixelsPerSecond })
      .set({ milliseconds: 0 })
      .toUTC();
    const bufferStartTime = chartStartTime
      .minus({ seconds: bufferSeconds })
      .set({ milliseconds: 0 })
      .toUTC();
    const bufferEndTime = chartEndDateTime
      .plus({ seconds: bufferSeconds })
      .set({ milliseconds: 0 })
      .toUTC();
    if (bufferStartTime >= bufferEndTime) throw "invalid buffer time range";

    if (!this.measurements?.data?.length) {
      return this.fetchMonitorMeasurements({
        starttime: bufferStartTime,
        endtime: bufferEndTime,
      });
    }
    // Crop the stored data to the buffer range.
    this._measurements.data =
      this.measurements?.data.filter(
        (m) =>
          m.dateTime >= bufferStartTime.minus({ seconds: 1 }) &&
          m.dateTime <= bufferEndTime.plus({ seconds: 1 }),
      ) || [];

    const data = this.measurements.data;
    const firstSampleTime = data.find((d) => d.dateTime)?.dateTime;
    const lastContinuousSampleTime = data
      .find((segment, i) => {
        if (i === data.lastIndex) return true;
        const nextSegment = data[i + 1];
        return (
          !nextSegment ||
          nextSegment.dateTime.diff(segment.dateTime, "seconds").seconds > 1
        );
      })
      ?.dateTime.toUTC();
    const lastSampleTime = lastContinuousSampleTime;

    const startBufferFilled =
      this.fetchedTimeRange?.start <= bufferStartTime ||
      firstSampleTime <= bufferStartTime;
    const endBufferFilled =
      lastSampleTime >= bufferEndTime.minus({ seconds: 1 });

    if (!startBufferFilled && !endBufferFilled) {
      return this.fetchMonitorMeasurements({
        starttime: bufferStartTime,
        endtime: bufferEndTime.plus({ seconds: 1 }),
      });
    } else if (!startBufferFilled) {
      return this.fetchMonitorMeasurements({
        starttime: bufferStartTime,
        endtime: DateTime.min(
          firstSampleTime,
          bufferEndTime.plus({ seconds: 1 }),
        ),
      });
    } else if (!endBufferFilled) {
      return this.fetchMonitorMeasurements({
        starttime: DateTime.max(
          lastSampleTime.plus({ seconds: 1 }),
          bufferStartTime,
        ).toUTC(),
        endtime: bufferEndTime,
      });
    } else {
      return { filled: true };
    }
  }

  handleDrift = () => {
    const dateTime = this._measurements?.data?.lastItem?.dateTime;
    if (!(dateTime?.isValid && this.chartEndTime?.isValid)) return;
    // reset delay time if it drifts too much.
    const actualDelay = dateTime
      .diff(this.chartEndTime, "seconds")
      .as("seconds");

    if (actualDelay <= 0 || actualDelay > this.liveModeDelaySeconds * 2) {
      this.chartEndTime = dateTime.minus({
        seconds: this.liveModeDelaySeconds,
      });
      this.originalChartEndTime = dateTime;
      this.animationStartTime = performance.now();
    }
  };
  updateLatency(newLatency) {
    if (!this.serverLatency) return (this.serverLatency = newLatency);
    if (
      Math.abs(newLatency - this.serverLatency) >
      this.liveModeDelaySeconds * 1000
    ) {
      this.serverLatency = newLatency;
    }
  }
  adjustToMonitorTime = ({ measurements, isLiveQuery, reset, serverTime }) => {
    // set the live-time, or use it to filter the data.
    if (!isLiveQuery)
      return this.dropEmptyFutureData({ measurements, serverTime });

    const lastDateTime = addDateTimeToSegment(
      measurements?.data?.lastItem,
    )?.dateTime;
    let _serverTime = lastDateTime?.plus({ seconds: 1 });
    if (!_serverTime?.isValid)
      _serverTime = DateTime.fromISO(measurements?.serverTimestamp);
    if (
      _serverTime?.isValid &&
      (hasWaveData(measurements) || reset === "provisional")
    ) {
      this.updateLatency(DateTime.local().diff(_serverTime));
    }
    // The provisional call is just to get the server time. We don't want to update measurements.
    if (reset === "provisional") throw new Error("provisional");

    return measurements;
  };

  async fetchMonitorMeasurements(query, reset) {
    // *** embedded + live mode is a "special" case, for which this function is not to be run
    if (this.isEmbedded && this.liveMode) return;
    if (this.isEmptyBed) return;
    // normalize query
    try {
      const { endtime, starttime } = query;
      Object.entries({ endtime, starttime }).forEach(([param, time]) => {
        if (typeof time === "string") {
          query[param] = DateTime.fromISO(time);
        }
      });
      if (starttime >= endtime) throw "starttime >= endtime";
    } catch (e) {
      console.warn("invalid date query", e);
    }

    const isHistorical = !!query.endtime;
    const isLiveQuery = !!(
      typeof query.seconds !== "undefined" ||
      typeof query.bookmark !== "undefined"
    );
    if (!isHistorical && !isLiveQuery) throw "invalid query";
    if (isHistorical && !this.serverLatency) {
      // this will provide serverLatency and serverTime.
      await this.fetchMonitorMeasurements({ seconds: 1 }, "provisional");
    }

    if (isHistorical) {
      query.endtime = DateTime.min(query.endtime, this.serverTime)
        .toUTC()
        .toISO();
    }
    this.loadingMeasurements = true;

    const getMonitorMeasurementParams = {
      asmpid: this.asmpid,
      ...query,
    };

    if (this.abortController) {
      this.abortController.abort();
      this.abortController = undefined;
    }
    const controller = new AbortController();
    this.abortController = controller;
    const serverTime = this.serverTime;

    return getMonitorMeasurements(getMonitorMeasurementParams, {
      controller,
      quiet: reset === "provisional",
    })
      .then((measurements) => filterWaveData(measurements, query))
      .then((measurements) =>
        this.adjustToMonitorTime({
          measurements,
          isLiveQuery,
          reset,
          serverTime,
        }),
      )
      .then(
        action((measurements) => {
          this.measurements = {
            ...measurements,
            reset,
          };
          return measurements;
        }),
      )
      .then((measurements) => {
        this.fetchedTimeRange = this.updateFetchedTimeRange({
          measurements,
          query: getMonitorMeasurementParams,
          reset,
        });
      })
      .catch((error) => {
        if (error.message === "provisional") return;
        if (error.ABORT_ERR || error.name === "AbortError")
          return console.warn(error);
        console.error(error);
        // insert some fake blank data, so the monitor keeps moving.
        if (isLiveQuery && this.liveMode) {
          this.insertProvisionalBlanks();
        }
      })
      .finally(() => {
        if (this.abortController === controller) {
          this.abortController = undefined;
          this.loadingMeasurements = false;
          this.loaded = true;
        }
      });
  }

  insertProvisionalBlanks() {
    const { serverTime } = this;
    if (!serverTime?.isValid) return;
    const lastDateTime = this.measurements?.data?.lastItem?.dateTime;
    if (!lastDateTime?.isValid) return;
    const missingSeconds = Math.floor(
      serverTime.diff(lastDateTime, "seconds").as("seconds"),
    );
    if (!missingSeconds > 0) return;
    const provisionalData = Array(missingSeconds)
      .fill({
        provisional: true,
        waveData: [],
        eventData: [],
        discreteData: [],
      })
      .map((d, i) => {
        const dateTime = lastDateTime.plus({ seconds: i + 1 });
        return {
          ...d,
          dateTime,
          timestamp: dateTime.toISO(),
        };
      });
    this.measurements = {
      data: provisionalData,
      bookmark: this.measurements.bookmark,
    };
  }

  get storedTimeRange() {
    try {
      const start = this.measurements?.data?.[0]?.dateTime;
      const end = this.measurements?.data?.findLast(
        (d) => d.timestamp,
      )?.dateTime;
      return Interval.fromDateTimes(start, end);
    } catch (e) {
      return Interval.invalid("storedTimeRange failed", e);
    }
  }
  getRequestedTimeRange({ starttime, endtime }) {
    try {
      return Interval.fromDateTimes(toLuxon(starttime), toLuxon(endtime));
    } catch (e) {
      return Interval.invalid("getRequestedTimeRange failed", e);
    }
  }

  /**
   * Sets a property `fetchedTimeRange` of type Luxon Interval (not to be confused with a JS interval)
   * This is not the data available in cache, but the time range of data for which has been requested,
   * after the server response, adjusted by the time on the Monitor.
   * @param {object} measurements raw measurements  server
   * @param {object} query parameters used for fetching measurements
   * @param {boolean} reset flag to reset data
   * @returns measurements
   */
  updateFetchedTimeRange = ({ query, reset }) => {
    // `this.measurements` is sorted and transformed, while the argument `measurements` is the server response.
    const { storedTimeRange, fetchedTimeRange } = this;
    const requestedTimeRange = this.getRequestedTimeRange(query);
    if (requestedTimeRange?.isValid) {
      if (fetchedTimeRange?.isValid && !reset) {
        const intervals = Interval.merge([
          fetchedTimeRange,
          requestedTimeRange,
        ]);
        if (intervals.length === 1) {
          if (
            storedTimeRange?.isValid &&
            storedTimeRange.end < requestedTimeRange.end
          ) {
            // it didn't get the latest, because it's in the future.
            return fetchedTimeRange.union(storedTimeRange);
          }
          return intervals[0];
        }
      }
      if (!storedTimeRange?.isValid) return requestedTimeRange;
    }
    if (storedTimeRange?.isValid) return storedTimeRange;
    return fetchedTimeRange;
  };

  fetchQRS({ query = { qrs: "all", r: "all" }, model }) {
    this.loadingQrs = true;
    return Promise.all([postQrsProcessor({ query, model }), getAcwaVersion()])
      .then((responses) => {
        this.algoVersion = _.get(responses[1], "ecgMagicDllVersion");
        return responses[0];
      })
      .finally(() => (this.loadingQrs = false));
  }

  get zoom() {
    return this.pixelsPerSecond / 50;
  }

  set zoom(z) {
    const endTime = this.chartEndTime;
    const originalPixelsPerSecond = this.pixelsPerSecond;
    this.pixelsPerSecond = Math.max(z * 50, this.minPixelsPerSecond);
    this.offsetY *= this.pixelsPerSecond / originalPixelsPerSecond;
    this.chartEndTime = endTime;
  }

  PR;
  QRS;
  QT;
  caliperDistance = "";

  getQTc() {
    const hr = this.getHeartRate();
    if (!hr || !(this.QT > 0)) return undefined;
    const hrInterval = 60 / _.get(hr, "value");
    return this.QT / Math.sqrt(hrInterval);
  }

  calcSnippetDistance(i, j) {
    if (
      !this.markers[i] ||
      !this.markers[j] ||
      this.markers[i].muted ||
      this.markers[j].muted
    ) {
      return null;
    }
    return this.markers[i] &&
      this.markers[i].timestamp &&
      this.markers[j].timestamp
      ? DateTime.fromISO(this.markers[j].timestamp)
          .diff(DateTime.fromISO(this.markers[i].timestamp), "seconds")
          .as("seconds")
      : 0;
  }

  getSnippetPreview({ model }) {
    const fetchPreview = config.pm.jpegPreview
      ? fetchJpgSnippetPreview
      : getSnippetPreview;
    return fetchPreview({ query: { asmpid: this.asmpid }, model });
  }

  saveSnippet({ model, query = {} }) {
    return savePMSnippet({ query: { asmpid: this.asmpid, ...query }, model });
  }

  getWaveGroups({ single }) {
    const waveGroups = this.config.waveGroups;
    if (waveGroups) {
      if (!this.selectedWaveform1key)
        this.selectedWaveform1key = waveGroups[0]?.key;
      if (!this.selectedWaveform2key)
        this.selectedWaveform2key = single ? "" : waveGroups[1]?.key;
    }
  }

  getDiscreteColors(config) {
    config.discreteGroups.forEach((discrete) => {
      if (discrete.schemeColors) return;
      // This assumes the color rendering on white.
      const color = tinycolor(`#${discrete.textColor.slice(-6)}`);
      discrete.schemeColors = {
        lightMode: makeReadableColor({ color }).toHexString(),
        darkMode: makeReadableColor({ color, darkMode: true }).toHexString(),
      };
    });
  }

  /**
   * Takes response from fetchMonitorConfig and generates wave form colors
   *
   * @param {*} config fetchMonitorConfig response
   * @return {*}
   * @memberof MonitorStore
   */
  generateColorConfig(config) {
    if (config) {
      config.waveGroups && handleMonitorConfigResponse(config);
      this.getDiscreteColors(config);

      this.config = config;
      return config;
    }
  }

  async getMonitorConfig({ suppressLoading } = {}) {
    if (!suppressLoading) this.loading = true;
    return await fetchMonitorConfig(this.asmpid)
      .then((config) => this.generateColorConfig(config))
      .finally(() => (this.loading = false));
  }

  fetchTrends = ({ key, starttime }, options) => {
    return getMonitorTrends(
      {
        keys: key,
        asmpid: this.asmpid,
        starttime,
      },
      options,
    ).then((res) => {
      return res.trends;
    });
  };

  fetchDiagnoses() {
    return getSnippetEventDescriptionLibrary().then(
      (descriptions) =>
        (this.diagnoses = transformDiagnoses(
          descriptions,
          this.showStatementCodes,
        )),
    );
  }

  postOpsLog(model) {
    return postOpsLog(model);
  }

  getMonitorEvents({ refresh, query = {} } = {}) {
    const { asmpid } = this;
    if (!refresh) this.loadingEvents = true;
    return getMonitorEvents({
      asmpid,
      ...query,
    })
      .then((res) => {
        this.events = res;
      })
      .finally(() => {
        this.loadingEvents = false;
      });
  }

  get chartEndTimeAdjusted() {
    const { chartEndTime, serverLatency } = this;
    try {
      return chartEndTime.plus(serverLatency);
    } catch (e) {
      if (chartEndTime?.isValid) return chartEndTime;
      return DateTime.utc();
    }
  }

  inFetchedTimeRange({ start }) {
    try {
      return (
        start &&
        this.fetchedTimeRange?.intersection(
          Interval.fromDateTimes(start, this.chartEndTime),
        )
      );
    } catch (e) {
      // luxon
    }
  }

  fetchAhiDescriptions() {
    getAhiDescriptions()
      .then((data) => (this.ahiDescriptions = data))
      .catch(() => {
        console.error("getAhiDescriptions error");
      });
  }

  fetchAhiData() {
    getPatientAhiData(this.asmpid)
      .then((data) => {
        this.ahiData = data?.result?.[0]?.value;
      })
      .catch(() => {
        console.warn("getPatientAhiData error");
      });
  }

  snippetCreationLog = [];
  logSnippetCreation({ stage } = {}) {
    const now = DateTime.now();
    if (stage === "started") {
      this.snippetCreationLog.push({ started: now });
    } else if (stage === "completed") {
      this.snippetCreationLog.push({ completed: now });
    }
    let durationInSeconds;

    if (stage === "completed") {
      try {
        // diff in second between the first and last log
        durationInSeconds = this.snippetCreationLog[1].completed
          .diff(this.snippetCreationLog[0].started, "seconds")
          .as("seconds");
        durationInSeconds = Math.round(durationInSeconds);
      } catch (e) {
        // log the error?:
        durationInSeconds = `ERROR: ${e}`;
      }
    }

    this.postOpsLog({
      operationType: `snippetCreation${stage}`,
      extraContext: {
        stage,
        asmpid: this.asmpid,
        snippetCreationTimestamp: this.snippetCreationLog,
        durationInSeconds,
      },
    });
  }

  get serverTime() {
    return !isNaN(this.serverLatency)
      ? DateTime.utc().minus(this.serverLatency)
      : null;
  }

  dropEmptyFutureData({ measurements, serverTime = this.serverTime }) {
    // TODO: remove this in the next release, after the server is fixed.
    // Filter out fake empty data from the future.
    // This appears to only occur on historical calls that request an endtime past the current time
    // on the monitor (serverTime).
    const lastSegment = measurements?.data?.lastItem;
    if (!lastSegment) return measurements;
    const lastDateTime = addDateTimeToSegment(lastSegment).dateTime;
    // If there is no data in the future, return the original data.
    if (lastDateTime < serverTime?.minus({ seconds: 10 })) return measurements;
    const lastValidIndex = measurements.data.findLastIndex(
      (segment) =>
        segment.waveData?.length ||
        segment.eventData?.length ||
        segment.discreteData?.length,
    );
    measurements.data = measurements.data.slice(0, lastValidIndex + 1);
    return measurements;
  }
}

export function transformDiagnoses(descriptions, showStatementCodes) {
  return descriptions.statementLibrary.map((d) => {
    return {
      label: showStatementCodes ? `${d.code} - ${d.text}` : d.text,
      value: d.code,
    };
  });
}

export function handleMonitorConfigResponse(config) {
  if (config) {
    config.waveGroups.forEach((group) => {
      group.maxRange = Number(group.maxRange);
      group.minRange = Number(group.minRange);
      group.disabled = false;
      group.lineColor = limitGamut(group.lineColor).toHexString();
    });
    return config;
  }
}

export function fetchMonitorConfig(asmpid) {
  return getMonitorConfig(asmpid).then((config) =>
    handleMonitorConfigResponse(config),
  );
}

export function filterWaveData(measurements) {
  if (measurements) {
    measurements.data?.forEach((segment) =>
      segment.waveData.forEach(
        (wave) =>
          (wave.data = wave.data?.map((point) =>
            point > -32000 && point < 32000 ? point : null,
          )),
      ),
    );
    return measurements;
  }
}

/**
 * Returns a compliant readable color for reading on white or black.
 * @param {color} color
 * @param {boolean} darkMode - white or black background
 * @returns {tinycolor} color
 */
function makeReadableColor({ color, darkMode }) {
  let _color = tinycolor(color);
  if (!_color.isValid()) return;
  const bg = darkMode ? tinycolor("#000") : tinycolor("#fff");
  let i = 0;
  while (tinycolor.isReadable(_color, bg) === false && i++ < 255) {
    if (darkMode) {
      _color = _color.lighten(1);
    } else {
      _color = _color.darken(1);
    }
  }
  return _color;
}

function addDateTimeToSegment(segment) {
  if (segment && !segment.dateTime) {
    segment.dateTime = DateTime.fromISO(segment.timestamp).toUTC();
  }
  return segment;
}

export function hasWaveData(measurements) {
  return !!measurements?.data?.find((d) => d.waveData?.length);
}
